import { useEffect, useRef, useCallback, useMemo, memo } from 'react'
import { createPortal } from 'react-dom'
import ModalErrorBoundary from './ModalErrorBoundary'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showCloseButton?: boolean
  closeOnOverlayClick?: boolean
  className?: string
  'aria-describedby'?: string
  'aria-labelledby'?: string
}

const Modal = memo<ModalProps>(({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  className = '',
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)
  const titleId = useRef(`modal-title-${Math.random().toString(36).substr(2, 9)}`)

  // Memoized responsive size classes for the modal
  const sizeClasses = useMemo(() => ({
    sm: 'max-w-sm w-full mx-4 sm:mx-auto',
    md: 'max-w-md w-full mx-4 sm:mx-auto',
    lg: 'max-w-2xl w-full mx-4 sm:mx-auto',
    xl: 'max-w-4xl w-full mx-4 sm:mx-auto'
  }), [])

  // Enhanced focus trap and keyboard handling
  const getFocusableElements = useCallback(() => {
    if (!modalRef.current) return []

    const focusableSelectors = [
      'button:not([disabled])',
      '[href]:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
      '[contenteditable="true"]'
    ].join(', ')

    return Array.from(modalRef.current.querySelectorAll(focusableSelectors)) as HTMLElement[]
  }, [])

  const trapFocus = useCallback((event: KeyboardEvent) => {
    const focusableElements = getFocusableElements()
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  }, [getFocusableElements])

  useEffect(() => {
    if (!isOpen) return

    // Store the previously focused element
    previousActiveElement.current = document.activeElement as HTMLElement

    // Prevent body scroll when modal is open
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Focus the modal when it opens
    const focusModal = () => {
      const focusableElements = getFocusableElements()
      if (focusableElements.length > 0) {
        // Focus the first focusable element that's not the close button
        const nonCloseElements = focusableElements.filter(el =>
          !el.getAttribute('aria-label')?.includes('Close modal')
        )
        if (nonCloseElements.length > 0) {
          nonCloseElements[0].focus()
        } else {
          focusableElements[0].focus()
        }
      } else if (modalRef.current) {
        modalRef.current.focus()
      }
    }

    // Handle keyboard events
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      // Focus trap logic
      if (event.key === 'Tab') {
        trapFocus(event)
      }
    }

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown)

    // Focus the modal after a brief delay to ensure it's rendered
    const timeoutId = setTimeout(focusModal, 100)

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      clearTimeout(timeoutId)

      // Restore body scroll
      document.body.style.overflow = originalOverflow

      // Restore focus to the previously active element
      if (previousActiveElement.current && document.contains(previousActiveElement.current)) {
        previousActiveElement.current.focus()
      }
    }
  }, [isOpen, onClose, getFocusableElements, trapFocus])

  // Memoized overlay click handler
  const handleOverlayClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose()
    }
  }, [closeOnOverlayClick, onClose])

  // Memoized modal classes
  const modalClasses = useMemo(() => `
    relative transform overflow-hidden bg-white text-left shadow-xl transition-all
    w-full h-full sm:h-auto sm:rounded-lg sm:my-8
    ${sizeClasses[size]}
    ${className}
  `, [sizeClasses, size, className])

  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby={ariaLabelledBy || titleId.current}
      aria-describedby={ariaDescribedBy}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Modal container - Enhanced responsive design */}
      <div className="flex min-h-full items-end justify-center text-center sm:items-center sm:p-4">
        {/* Mobile: Full screen on small devices, centered on larger */}
        <div
          ref={modalRef}
          tabIndex={-1}
          className={modalClasses}
          style={{
            // Ensure high contrast mode compatibility
            border: '1px solid transparent'
          }}
        >
          <ModalErrorBoundary>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-4 sm:px-6 border-b border-gray-200">
              <h3
                id={ariaLabelledBy || titleId.current}
                className="text-lg font-semibold text-gray-900 pr-8"
              >
                {title}
              </h3>
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className="
                    absolute top-4 right-4 rounded-md bg-white text-gray-400 
                    hover:text-gray-500 focus:outline-none focus:ring-2 
                    focus:ring-indigo-500 focus:ring-offset-2
                    p-1 transition-colors
                    high-contrast:border high-contrast:border-gray-600
                  "
                  aria-label="Close modal"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Content - Enhanced responsive padding */}
            <div className="px-4 py-4 sm:px-6 overflow-y-auto max-h-[calc(100vh-8rem)] sm:max-h-[calc(100vh-12rem)]">
              {children}
            </div>
          </ModalErrorBoundary>
        </div>
      </div>
    </div>
  )

  // Render modal in a portal
  return createPortal(modalContent, document.body)
})

Modal.displayName = 'Modal'

export default Modal
export { Modal }